// Head Soccer 2D 60Hz Server Engine (2 Players + Spectators)
export class SoccerEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.width = 800;
    this.height = 460;
    this.groundY = 400;
    this.players = {};
    this.ball = { x: 400, y: 300, vx: 0, vy: 0, r: 14 };
    this.scores = { p1: 0, p2: 0 };
    this.matchTimer = 90;
    this.loopInterval = null;
  }

  start() {
    const shouldFillBots = this.room.settings?.fillBots !== false;
    const pList = this.room.players.slice(0, 2).map(p => ({ id: p.id, name: p.name, isBot: !!p.isBot }));

    if (pList.length === 1 && shouldFillBots) {
      pList.push({ id: 'bot_soccer', name: 'Striker Bot ⚽', isBot: true });
    }

    this.players = {};
    this.scores = { p1: 0, p2: 0 };
    this.matchTimer = 90;

    // P1 Left
    this.players[pList[0].id] = {
      id: pList[0].id,
      name: pList[0].name,
      team: 'p1',
      x: 180,
      y: this.groundY,
      vx: 0,
      vy: 0,
      isGrounded: true,
      color: '#ef4444',
      isBot: pList[0].isBot || false,
      input: { left: false, right: false, jump: false, kick: false }
    };

    // P2 Right
    if (pList[1]) {
      this.players[pList[1].id] = {
        id: pList[1].id,
        name: pList[1].name,
        team: 'p2',
        x: 620,
        y: this.groundY,
        vx: 0,
        vy: 0,
        isGrounded: true,
        color: '#38bdf8',
        isBot: pList[1].isBot || false,
        input: { left: false, right: false, jump: false, kick: false }
      };
    }

    this.resetBall();

    this.io.to(this.room.code).emit('soccer_started', {
      players: this.players,
      scores: this.scores,
      timer: this.matchTimer
    });

    if (this.loopInterval) clearInterval(this.loopInterval);
    this.loopInterval = setInterval(() => this.update(), 1000 / 60);
  }

  resetBall() {
    this.ball.x = 400;
    this.ball.y = 200;
    this.ball.vx = 0;
    this.ball.vy = -3;
  }

  handleInput(playerId, input) {
    const p = this.players[playerId];
    if (p) p.input = input;
  }

  update() {
    this.matchTimer -= 1 / 60;

    // Update Players
    for (const id in this.players) {
      const p = this.players[id];

      // Bot AI
      if (p.isBot) {
        if (p.team === 'p2') {
          // Track ball
          if (this.ball.x > 380) {
            p.input.left = this.ball.x < p.x - 10;
            p.input.right = this.ball.x > p.x + 10;
            p.input.jump = this.ball.y < p.y - 40 && Math.abs(this.ball.x - p.x) < 50;
            p.input.kick = Math.abs(this.ball.x - p.x) < 40;
          } else {
            p.input.right = p.x < 600;
            p.input.left = p.x > 640;
          }
        }
      }

      // Movement
      const speed = 5.2;
      p.vx = (p.input.right ? speed : 0) - (p.input.left ? speed : 0);

      // Jump
      if (p.input.jump && p.isGrounded) {
        p.vy = -12;
        p.isGrounded = false;
      }

      // Gravity
      p.vy += 0.55;
      p.x += p.vx;
      p.y += p.vy;

      // Ground collision
      if (p.y >= this.groundY) {
        p.y = this.groundY;
        p.vy = 0;
        p.isGrounded = true;
      }

      // Pitch Boundaries
      p.x = Math.max(30, Math.min(this.width - 30, p.x));

      // Player Kick / Head Ball Collision
      const dx = this.ball.x - p.x;
      const dy = this.ball.y - (p.y - 20);
      const dist = Math.hypot(dx, dy);

      if (dist < p.isBot ? 36 : 38) {
        const nx = dx / dist;
        const ny = dy / dist;
        const kickPower = p.input.kick ? 15 : 9;
        this.ball.vx = nx * kickPower + (p.team === 'p1' ? 3 : -3);
        this.ball.vy = ny * kickPower - 4;
      }
    }

    // Ball Physics
    this.ball.vy += 0.45; // Gravity
    this.ball.x += this.ball.vx;
    this.ball.y += this.ball.vy;
    this.ball.vx *= 0.99;

    // Ball Floor Bounce
    if (this.ball.y >= this.groundY - this.ball.r) {
      this.ball.y = this.groundY - this.ball.r;
      this.ball.vy = -this.ball.vy * 0.75;
      if (Math.abs(this.ball.vy) < 1) this.ball.vy = 0;
    }

    // Ball Ceiling & Wall Bounces
    if (this.ball.y <= 20) { this.ball.y = 20; this.ball.vy = -this.ball.vy * 0.75; }
    if (this.ball.x <= 15) { this.ball.x = 15; this.ball.vx = -this.ball.vx * 0.75; }
    if (this.ball.x >= this.width - 15) { this.ball.x = this.width - 15; this.ball.vx = -this.ball.vx * 0.75; }

    // Goal Detection (Left Goal: x < 70, y > 280; Right Goal: x > 730, y > 280)
    if (this.ball.y > 280) {
      if (this.ball.x < 60) {
        // Goal for P2!
        this.scores.p2++;
        this.io.to(this.room.code).emit('soccer_goal', { scorer: 'p2', scores: this.scores });
        this.resetBall();
      } else if (this.ball.x > this.width - 60) {
        // Goal for P1!
        this.scores.p1++;
        this.io.to(this.room.code).emit('soccer_goal', { scorer: 'p1', scores: this.scores });
        this.resetBall();
      }
    }

    this.io.to(this.room.code).emit('soccer_state', {
      players: this.players,
      ball: this.ball,
      scores: this.scores,
      timer: Math.max(0, Math.ceil(this.matchTimer))
    });

    if (this.matchTimer <= 0) {
      clearInterval(this.loopInterval);
      const pArr = Object.values(this.players);
      const winner = this.scores.p1 > this.scores.p2 ? pArr.find(p => p.team === 'p1') : pArr.find(p => p.team === 'p2');
      this.io.to(this.room.code).emit('soccer_gameOver', { winner, scores: this.scores });
    }
  }

  destroy() {
    if (this.loopInterval) clearInterval(this.loopInterval);
  }
}
